import type { ObjectId } from 'mongodb';

import { AccountMilestoneStatus } from './AccountMilestones';
import type { Meta } from './Entities';
import { OrgV2PaymentPlanChangeStatus } from './OrganizationSettingsV2';
import { OrganizationType } from './OrganizationTypes';
import type { PaymentPlanEventSetting } from './PaymentPlans';
import { ProgramType } from './ProgramTypes';
import type { Actor, UserWithDate } from './UserTypes';
import type { RedactedWarrantyContractSkuTermMapping, WarrantyContractSkuTermMapping } from './Warranties';

export enum OrganizationStatus {
  active = 'active',
  offBoarded = 'offBoarded',
  noNewSales = 'noNewSales',
  readOnly = 'readOnly',
}

export enum StateLicenseStatus {
  active = 'active',
  inactive = 'inactive',
  suspended = 'suspended',
}

export interface StateLicenseNumber {
  state: string;
  contractorLicenseNumber: string;
  additionalLicenseNumbers?: string;
  monetaryLimit?: number;
  programType: ProgramType;
  status: StateLicenseStatus;
}

export const OrganizationStatusLabels: Record<OrganizationStatus | 'unknown', string> = {
  active: 'Active',
  offBoarded: 'Offboarded',
  noNewSales: 'No New Sales',
  readOnly: 'Read Only',
  unknown: 'Unknown Status',
};

export interface AuthorizedSigner {
  name: string;
  email: string;
  title: string;
}

export interface NewOrganization {
  alias: string;
  parentAlias?: string;
  name: string;
  status?: OrganizationStatus;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phoneNumber?: string;
  licenses?: Array<StateLicenseNumber>;
  insuranceCompanyName?: string;
  insuranceCompanyPhoneNumber?: string;
  email?: string;
  federalEmployerId?: string;
  programTypes?: ProgramType[];
  organizationTypes?: OrganizationType[];
  extendedWarrantyDealerInformation?: Array<ExtendedWarrantyDealerInformation>;
  accountBundlingEnabled?: boolean;
}

export interface CEDBatteryRate {
  id: string;
  manufacturer: string;
  model: string;
  billingRate: number;
}

/**
 * @deprecated
 */
export interface OrganizationCEDSettings {
  cedSplitPayEnabled: boolean;
  cedInstallerIdentifier?: string;
  spaPricingEnabled?: boolean;
  pvBillingRate?: number;
  programName?: string;
  batteryRates?: CEDBatteryRate[];
  cedSplitPayEnabledDate?: Date;
}

export enum OrganizationExternalReferenceType {
  dispatchSupplier = 'dispatchSupplier',
}
export interface OrganizationExternalReference {
  id: string;
  type: OrganizationExternalReferenceType;
}

export interface OrganizationStatusChangeHistoryEntry {
  oldStatus?: OrganizationStatus;
  newStatus: OrganizationStatus;
  description?: string;
  changedBy: {
    id?: string;
    username?: string;
  };
  changedAt: Date;
}

export interface OrganizationDocument {
  id: ObjectId;
  alias: string;
  parentAlias?: string;
  name: string;
  pricingTierId?: string;
  status?: OrganizationStatus;
  visibleOrganizations: Array<string>;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phoneNumber?: string;
  licenses?: Array<StateLicenseNumber>;
  insuranceCompanyName?: string;
  insuranceCompanyPhoneNumber?: string;
  email?: string;
  federalEmployerId?: string;
  /**
   * @deprecated
   */
  cedSettings?: OrganizationCEDSettings;
  authorizedSigners?: Array<AuthorizedSigner>;
  externalReferenceIds?: OrganizationExternalReference[];
  programTypes?: ProgramType[];
  organizationTypes?: OrganizationType[];
  meta?: Meta;
  extendedWarrantyDealerInformation?: Array<ExtendedWarrantyDealerInformation>;
  salesforceData?: OrganizationSalesforceData;
  isPayeeContractor?: boolean;
  statusChangeHistory?: OrganizationStatusChangeHistoryEntry[];
}

export interface ExtendedWarrantyDealerInformation {
  warrantyProvider: string;
  dealerLicenseNumber: string;
  isPreferredProvider: boolean;
  contractSkuTermMappings?: WarrantyContractSkuTermMapping[];
  /**
   * @deprecated Use contractSkuTermMappings for program and term-specific SKUs.
   */
  contractSku?: string;
}

// Shape returned to non-internal users: contractSku/term-level data stripped.
export type RedactedExtendedWarrantyDealerInformation = Omit<
  ExtendedWarrantyDealerInformation,
  'contractSku' | 'contractSkuTermMappings'
> & {
  contractSkuTermMappings?: RedactedWarrantyContractSkuTermMapping[];
};

export interface OrganizationSalesforceData {
  salesforceId: string;
  lrAccountRepName: string;
  lrTier: string;
  lastUpdated: Date;
}

export enum OrganizationSearchField {
  alias = 'alias',
  name = 'name',
}

export type ProposedPaymentPlanAcceptedBy = UserWithDate | string[];

export interface OrganizationPaymentPlanDocument {
  id: ObjectId;
  organizationId: string; // I think we will want this to make the hierarchy lookup easier
  programType: ProgramType;
  paymentPlanId: ObjectId;
  paymentPlanName: string;
  effectiveDate: Date;
  paymentSchedule: PaymentPlanEventSetting[];
  sentUsersPaymentPlanProposed: string[];
  proposedPaymentPlanAcceptedBy?: ProposedPaymentPlanAcceptedBy;
  setBy?: Actor;
  meta?: Meta;
}

export type ImpactedAccountSummary = {
  id: string;
  name: string;
  currentMilestone: string;
  milestoneStatus: AccountMilestoneStatus;
};

export type OrgAccountsImpacted = {
  organizationId: string;
  total: number;
  accounts: ImpactedAccountSummary[];
};

/**
 * Entity types carried by the Finance → Customers org sync wire contract.
 * Shared between sabal (producer) and the customers service (consumer,
 * separate repo) — the string values are the wire contract, not an internal
 * implementation detail.
 */
export enum OrganizationSyncEntityType {
  organization = 'organization',
  organizationCoverageArea = 'organizationCoverageArea',
}

/** Operations carried by the org sync wire contract. Upsert-only — there is no delete op. */
export enum OrganizationSyncOp {
  upsert = 'upsert',
}

export interface PendingOrganizationPaymentPlanChanges {
  id: ObjectId;
  organizationId: string;
  createdByName: string;
  currentPaymentPlanId: ObjectId | null;
  currentPaymentPlanName: string | null;
  organizationName: string;
  effectiveDate: Date;
  createdAt: Date;
  createdBy: string;
  proposedPlanId: ObjectId;
  proposedPaymentPlanName: string;
  programType: ProgramType[];
  changeStatus: OrgV2PaymentPlanChangeStatus;
  proposedPaymentPlanAcceptedBy?: UserWithDate;
  currentAndProposedDifferences: Array<{
    milestone: string;
    currentPercentage: string;
    proposedPercentage: string;
    currentPausedDays?: number;
    proposedPausedDays?: number;
  }>;
}

export interface OrgHardwareExceptionSummary {
  alias: string;
  name: string;
  hasCategoryException: boolean;
  hasSpecificException: boolean;
  hasChildOrganizations: boolean;
}

export interface ListOrgHardwareExceptionsParams {
  search?: string;
  limit: number;
  offset: number;
}

export interface OrgHardwareExceptionsResponse {
  data: OrgHardwareExceptionSummary[];
  total: number;
}
